import { useTranslation } from "react-i18next";
import { Menu } from "lucide-react";
import type { ReactNode } from "react";
import type { FeatureNavNode, StoredAuthUser } from "@nasam/core";
import { cn, TooltipProvider } from "@nasam/ui";
import { useBreadcrumbStore } from "@nasam/core";
import { firstLeafPath } from "./AppShell";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { navNodeTitle } from "./navNodeTitle";

const TOP_BAR_ICON_BUTTON =
  "flex size-9 items-center justify-center rounded-md text-text transition-colors duration-150 ease-emphasized hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary";

const CHILD_SEGMENT_CLASSES = "text-body-caption-m text-text";

// Added on top of CHILD_SEGMENT_CLASSES for any activatable (clickable
// ancestor) segment, root included -- the root has no special fixed styling
// of its own anymore, it just reads like any other ancestor segment.
const CHILD_LINK_EXTRA_CLASSES =
  "cursor-pointer transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded-sm";

// The active (last, current-page) segment -- whichever one that is, root
// included when it's alone -- reads bold, coloured and underlined, but never
// as a link (it's already the page you're on).
const ACTIVE_SEGMENT_CLASSES = "text-body-xl-b-ar font-bold text-primary underline";

export interface TopBarProps {
  readonly onOpenMobileNav?: (() => void) | undefined;
  readonly currentRailNode?: FeatureNavNode | undefined;
  readonly activeNavNode?: FeatureNavNode | undefined;
  /** Picks the same user-specific tab labels the drawer shows. */
  readonly user?: StoredAuthUser | null | undefined;
  readonly onNavigate: (path: string) => void;
  /** Feature-contributed top-bar chrome (see AppFeatureDefinition.shellSlots), rendered before the language switcher. */
  readonly topBarEndSlots?: readonly ReactNode[] | undefined;
}

interface BreadcrumbItem {
  readonly key: string;
  readonly testId: string;
  readonly label: string;
  readonly path: string | null;
  readonly isRoot: boolean;
}

/** The nodes from `node` down to `targetId`, both included, or null. */
function navChain(node: FeatureNavNode, targetId: string): FeatureNavNode[] | null {
  if (node.id === targetId) return [node];
  for (const child of node.children ?? []) {
    const rest = navChain(child, targetId);
    if (rest !== null) return [node, ...rest];
  }
  return null;
}

export function TopBar({
  currentRailNode,
  activeNavNode,
  user,
  onNavigate,
  onOpenMobileNav,
  topBarEndSlots = [],
}: TopBarProps) {
  const { t } = useTranslation();
  const trail = useBreadcrumbStore((state) => state.trail);

  const landingPath =
    currentRailNode !== undefined
      ? (currentRailNode.path ??
        (currentRailNode.children !== undefined
          ? firstLeafPath(currentRailNode.children)
          : null))
      : null;

  // Rail → every drawer group on the way → the page's own tab, so a page
  // inside a group reads e.g. "Home / Organizations / Companies".
  const chain =
    currentRailNode === undefined
      ? []
      : activeNavNode === undefined
        ? [currentRailNode]
        : (navChain(currentRailNode, activeNavNode.id) ?? [currentRailNode]);

  const items: BreadcrumbItem[] = [];
  chain.forEach((node, index) => {
    const label = navNodeTitle(node, t, user);
    // A rail that opens on a same-named tab would otherwise read
    // "Companies / Companies".
    if (items.at(-1)?.label === label) return;
    const isRoot = index === 0;
    const isPage = index === chain.length - 1;
    items.push({
      key: isRoot ? "root" : node.id,
      testId: isRoot
        ? "breadcrumb-root"
        : isPage
          ? "breadcrumb-child"
          : `breadcrumb-group-${String(index)}`,
      label,
      // A drawer group has no page of its own, so it reads as plain text --
      // as in the drawer, where it only opens and closes.
      path: isRoot ? landingPath : (node.path ?? null),
      isRoot,
    });
  });
  trail.forEach((segment, index) => {
    items.push({
      key: `trail-${String(index)}`,
      testId: `breadcrumb-trail-${String(index)}`,
      label: segment.label,
      path: segment.path ?? null,
      isRoot: false,
    });
  });

  // Mobile shows only one line: whatever is deepest, since that's where the
  // user actually is.
  const mobileTitle = items.at(-1)?.label ?? null;

  // A fillHeight route (e.g. tracking) renders its own header, so the
  // breadcrumb would just duplicate it — but the end cluster (bell, language
  // switcher) has no other desktop home, so only the breadcrumb <nav> drops.
  const hideBreadcrumb = activeNavNode?.fillHeight === true;

  return (
    <TooltipProvider delayDuration={200}>
      <header
        data-node-id="1098:44022"
        className="flex shrink-0 items-center justify-end gap-2 px-4 py-3 md:hidden"
      >
        <button
          type="button"
          data-testid="mobile-nav-trigger"
          aria-label={t("common:openNavigation")}
          onClick={onOpenMobileNav}
          className="flex size-10 shrink-0 items-center justify-center text-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
        >
          <Menu className="size-6" />
        </button>
        <span
          data-testid="mobile-page-title"
          className="min-w-0 flex-1 truncate text-heading-h6-semibold text-text"
        >
          {mobileTitle}
        </span>
        {/* Figma 1098:44022 shows only the title and hamburger, but the
            language switcher has no other home on mobile (the desktop-only
            header below is the sole place it otherwise lives) — drop the
            notification bell here since it's not load-bearing, but keep
            this one so switching languages stays possible on every screen. */}
        <LanguageSwitcher
          testId="mobile-language-switcher"
          className="flex size-10 shrink-0 items-center justify-center rounded-md text-text hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
          tooltipSide="bottom"
        />
      </header>
      <header
        data-testid="top-bar"
        className={cn(
          // Figma (e.g. 4818:116757): the bar sits 32px below the card's top.
          "mt-8 hidden h-10 shrink-0 items-center px-4 md:flex",
          hideBreadcrumb ? "justify-end" : "justify-between",
        )}
      >
        {!hideBreadcrumb && (
          <nav aria-label="Breadcrumb" className="flex items-center">
            <ol className="m-0 flex list-none items-center gap-2 p-0">
              {items.map((item, index) => {
                const isLast = index === items.length - 1;
                // The root stays a link even when it's the only (and so also
                // last) segment -- Figma parity, and the existing click-to-
                // navigate test depends on it.
                const isActivatable =
                  item.isRoot || (!isLast && item.path !== null);
                const className = isLast
                  ? ACTIVE_SEGMENT_CLASSES
                  : isActivatable
                    ? `${CHILD_SEGMENT_CLASSES} ${CHILD_LINK_EXTRA_CLASSES}`
                    : CHILD_SEGMENT_CLASSES;
                const currentPageProps = isLast
                  ? ({ "aria-current": "page" } as const)
                  : {};

                return (
                  <li key={item.key} className="flex items-center gap-2">
                    {index > 0 && (
                      <span
                        aria-hidden="true"
                        data-testid="breadcrumb-separator"
                        className="select-none text-text"
                      >
                        /
                      </span>
                    )}
                    {isActivatable ? (
                      <button
                        type="button"
                        data-testid={item.testId}
                        onClick={() => {
                          if (item.path !== null) onNavigate(item.path);
                        }}
                        className={className}
                        {...currentPageProps}
                      >
                        {item.label}
                      </button>
                    ) : (
                      <span
                        data-testid={item.testId}
                        className={className}
                        {...currentPageProps}
                      >
                        {item.label}
                      </span>
                    )}
                  </li>
                );
              })}
            </ol>
          </nav>
        )}

        <div className="flex items-center gap-2">
          {topBarEndSlots}

          <LanguageSwitcher
            className={TOP_BAR_ICON_BUTTON}
            tooltipSide="bottom"
          />
        </div>
      </header>
    </TooltipProvider>
  );
}
